import React, { useCallback, useMemo, useState } from "react";
import { Select, Spin, Tag, Typography } from "antd";
import { Hint } from "@/common/hint";
import { useSyncedState } from "@/hooks/useSyncedState";
import "@/assets/less/custom-input-select.less";

const { Text } = Typography;

const toKeys = (items) =>
  Array.isArray(items) ? items.filter(Boolean).map((x) => x.key) : [];

const MultipleSelect = ({
  options = [],
  title,
  onSelect,
  onClear,
  value,
  includeSearch = true,
  selectedOnInit = false,
  width = "100%",
  required,
  loading = false,
}) => {
  const initialSelection = toKeys(selectedOnInit);

  // Callers rebuild `selectedOnInit` inline on every render, so the sync is keyed on
  // its content rather than its identity. A null `value` clears the selection unless
  // `selectedOnInit` explicitly asks for one, so it is folded into the same key.
  const selectionKey = `${value == null ? "null" : "set"}|${initialSelection.join("\u0000")}`;
  const [selectedOptions, setSelectedOptions] = useSyncedState(
    selectionKey,
    () => initialSelection
  );
  const [isOpen, setIsOpen] = useState(false);

  // Notify outside the state updater so the callback is never double-invoked
  // when React re-runs the reducer (StrictMode, concurrent rendering).
  const emitSelection = useCallback(
    (next) => {
      setSelectedOptions(next);
      onSelect?.(next);
    },
    [onSelect, setSelectedOptions]
  );

  const handleDeselectAll = useCallback(() => {
    emitSelection([]);
    onClear?.();
  }, [emitSelection, onClear]);

  // `disabled` is a presentation flag only: these options stay selectable so
  // callers can react to a disabled pick (see applyDisabledFilterSelection).
  // `selectionDisabled` explicitly prevents selection, e.g. user-list headings.
  const selectOptions = useMemo(
    () =>
      options.filter(Boolean).map((option) => ({
        value: option.key,
        label: option.label,
        icon: option.icon,
        dimmed: !!option.disabled,
        disabled: !!option.selectionDisabled,
      })),
    [options]
  );

  const collapseToSelectedAll =
    options.length > 0 &&
    selectedOptions.length === options.length &&
    selectedOptions.length > 2;

  const renderTag = useCallback(({ label, closable, onClose }) => {
    const onPreventMouseDown = (event) => {
      event.preventDefault();
      event.stopPropagation();
    };

    return (
      <Tag
        variant="filled"
        onMouseDown={onPreventMouseDown}
        closable={closable}
        onClose={onClose}
        style={{ verticalAlign: "initial", fontWeight: "600" }}
      >
        {label}
      </Tag>
    );
  }, []);

  const renderOmittedValues = useCallback(
    (omittedValues) => {
      if (collapseToSelectedAll) {
        return <span style={{ marginLeft: "10px" }}>Selected All</span>;
      }

      const omittedLabels = omittedValues.map(({ label }) => label).join(", ");
      return (
        <Hint width="200px" popoverText={omittedLabels} placement="right">
          <Text style={{ width: width }} ellipsis>
            {omittedLabels}
          </Text>
        </Hint>
      );
    },
    [collapseToSelectedAll, width]
  );

  const renderOption = useCallback(
    (option) => (
      <div
        className="ellipsis-column"
        style={{ display: "flex", alignItems: "center", gap: "8px" }}
      >
        {option.data?.icon && <i className={option.data.icon}></i>}
        <div
          data-dimmed={option.data?.dimmed ? "true" : undefined}
          style={{ flex: "1", color: option.data?.dimmed ? "var(--dms-color-text-subtle)" : "inherit" }}
        >
          {option.label}
        </div>
      </div>
    ),
    []
  );

  const renderPopup = useCallback(
    (menu) => (
      <>
        <div
          className="multiple-select-action"
          role="button"
          onMouseDown={(event) => event.preventDefault()}
          onClick={handleDeselectAll}
        >
          <i className="ri-prohibited-line" />
          <div>Deselect All</div>
        </div>
        {menu}
      </>
    ),
    [handleDeselectAll]
  );

  const icon = isOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line";
  const iconColour = isOpen ? "var(--dms-color-link)" : "var(--dms-color-text-muted)";
  const hasSelection = selectedOptions.length > 0;
  const handleSuffixPointerDown = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsOpen((currentOpen) => !currentOpen);
  };

  return (
    <div
      className={`custom-input document-filter-multiple-select select-${isOpen ? "active" : "inactive"} ${
        isOpen || hasSelection ? "label-floated" : ""
      }`}
      style={{ width: width }}
    >
      <div className="select-content">
        <div className="select-wrapper">
          <span
            className={`select-label ${isOpen || hasSelection ? "label-float" : ""}`}
          >
            {title}
            {required && <span className="required-indicator">*</span>}
          </span>
          <div className="input-items">
            <Select
              aria-label={typeof title === "string" ? title : undefined}
              mode="multiple"
              size="small"
              open={isOpen}
              value={selectedOptions}
              options={selectOptions}
              onChange={emitSelection}
              onOpenChange={setIsOpen}
              optionRender={renderOption}
              tagRender={renderTag}
              maxTagPlaceholder={renderOmittedValues}
              maxTagCount={collapseToSelectedAll ? 0 : "responsive"}
              menuItemSelectedIcon={
                <i className="ri-check-line" style={{ color: "var(--dms-color-text)" }}></i>
              }
              popupRender={renderPopup}
              showSearch={includeSearch}
              optionFilterProp="label"
              loading={loading}
              notFoundContent={loading ? <Spin size="small" /> : null}
              placeholder={null}
              suffixIcon={
                <span
                  className="select-suffix-container"
                  onPointerDown={handleSuffixPointerDown}
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                  }}
                >
                  <i className={icon} style={{ color: iconColour }} />
                </span>
              }
              variant="borderless"
              virtual
              listItemHeight={32}
              listHeight={256}
              style={{ width: "100%", fontSize: "12px" }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default MultipleSelect;
